import { useState } from 'preact/hooks';
import { CloseIcon } from './Icons.jsx';

const VALID = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Asking for an address, once, without making it a wall.
 *
 * Shown at the top of a new conversation when the snippet passed no email —
 * the merchant's own page often knows who the visitor is and sends it in
 * `visitor.metadata`, in which case this never appears at all.
 *
 * Dismissing it does not mean "never": it collapses to a single line above the
 * composer, so someone who did not want to hand over an address before saying
 * anything can still do it once they see the conversation is going somewhere.
 * That matters more than usual here — an address is what lets an agent reply
 * to a visitor who has closed the tab.
 */
export function EmailCapture({ collapsed, onSave, onDismiss, onExpand }) {
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (event) => {
    event.preventDefault();
    const email = value.trim();
    if (!VALID.test(email)) {
      setError('That does not look like an email address.');
      return;
    }

    setBusy(true);
    setError(null);
    try {
      await onSave(email);
    } catch {
      // The conversation is unaffected either way, so this stays a small
      // inline failure rather than anything that interrupts the thread.
      setError('Could not save that just now.');
      setBusy(false);
    }
  };

  if (collapsed) {
    return (
      <button type="button" class="emailcapture__reopen" onClick={onExpand}>
        + Add your email for a reply
      </button>
    );
  }

  return (
    <form class="emailcapture" onSubmit={submit}>
      <div class="emailcapture__head">
        <div class="emailcapture__title">Where can we reach you?</div>
        <button
          type="button"
          class="emailcapture__dismiss"
          onClick={onDismiss}
          aria-label="Not now"
        >
          <CloseIcon size={12} />
        </button>
      </div>
      <div class="emailcapture__sub">
        So we can reply even if you close this window.
      </div>
      <div class="emailcapture__row">
        <input
          class="emailcapture__input"
          type="email"
          inputMode="email"
          autocomplete="email"
          placeholder="you@company.com"
          value={value}
          onInput={(e) => setValue(e.currentTarget.value)}
          aria-label="Your email address"
        />
        <button class="button" type="submit" disabled={busy || !value.trim()}>
          {busy ? 'Saving…' : 'Save'}
        </button>
      </div>
      {error && <div class="emailcapture__error">{error}</div>}
    </form>
  );
}

export default EmailCapture;
